import * as React from "react"
import { Plus, ArrowDownLeft, Pin, Wallet } from "lucide-react"
import { ExpenseCard } from "./expense-card"
import { CategorySummaryCard } from "./category-summary-card"
import { Button } from "@/components/ui/button"
import { Card, CardHeader } from "@/components/ui/card"
import { EmptyState } from "@/components/ui/empty-state"
import { ExpenseListSkeleton } from "@/components/ui/loading-state"
import { groupBy, formatCurrency } from "@/lib/utils"
import type { Transaction } from "@/api/schemas/transaction.schema"
import type { Expense } from "@/types/domain.types"
import type { CategoryBalance } from "@/effects/groups/group.service"
import { useCurrentUser } from "@/stores/auth.store"

interface TransactionListProps {
  transactions: Transaction[]
  categoryBalances?: CategoryBalance[]
  isLoading?: boolean
  isError?: boolean
  onAddExpense?: () => void
  onEditExpense?: (expense: Expense) => void
  onDeleteExpense?: (expenseId: string) => void
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  groupBy?: 'date' | 'type' | 'none'
  showFilters?: boolean
  /** Transaction id to anchor + briefly highlight (e.g. from search / deep link). */
  highlightId?: string
}

const SettlementCard: React.FC<{ transaction: Transaction }> = ({ transaction }) => {
  const title = transaction.kind && transaction.kind !== 'manual'
    ? 'Pool Adjustment'
    : 'Settlement';

  return (
    <Card className="transition-colors hover:bg-muted/20 border-green-200 dark:border-green-900">
      <CardHeader className="pb-3 px-3 sm:px-6">
        <div className="flex items-start sm:items-center justify-between gap-2">
          {/* Left side: Icon and details */}
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            {/* Icon - smaller on mobile */}
            <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-green-500/10 flex-shrink-0">
              <ArrowDownLeft className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
            {/* Details */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm sm:text-base text-green-600 dark:text-green-400">
                {title}
              </h3>
              <div className="mt-1">
                <span className="text-xs sm:text-sm text-muted-foreground line-clamp-1">
                  {transaction.user?.name || transaction.from_user?.name || 'Unknown'} → {transaction.to_user?.name || 'Unknown'}
                </span>
              </div>
            </div>
          </div>
          {/* Right side: Amount and date */}
          <div className="text-right flex-shrink-0">
            <div className="font-semibold text-sm sm:text-base text-green-600 dark:text-green-400">
              -{formatCurrency(Math.abs(transaction.displayAmount || transaction.amount), transaction.currency || 'EUR')}
            </div>
            <div className="text-xs text-muted-foreground">
              {transaction.date ? new Date(transaction.date).toLocaleDateString() : 'No date'}
            </div>
          </div>
        </div>
        {transaction.description && (
          <p className="text-xs sm:text-sm text-muted-foreground mt-2">{transaction.description}</p>
        )}
      </CardHeader>
    </Card>
  )
}

const PoolEventCard: React.FC<{ transaction: Transaction }> = ({ transaction }) => {
  const isInsert = transaction.event_type === 'insert'
  const title = isInsert ? 'Pool Insert' : 'Pool-Funded Expense'
  const amountPrefix = isInsert ? '+' : ''

  return (
    <Card className="transition-colors hover:bg-muted/20 border-sky-200 dark:border-sky-900">
      <CardHeader className="pb-3 px-3 sm:px-6">
        <div className="flex items-start sm:items-center justify-between gap-2">
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-sky-500/10 flex-shrink-0">
              <Wallet className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm sm:text-base text-sky-700 dark:text-sky-300">
                {title}
              </h3>
              <div className="mt-1">
                <span className="text-xs sm:text-sm text-muted-foreground line-clamp-1">
                  {transaction.pool_name || 'Shared pool'} {transaction.user?.name ? `• ${transaction.user.name}` : ''}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="font-semibold text-sm sm:text-base text-sky-700 dark:text-sky-300">
              {amountPrefix}{formatCurrency(Math.abs(transaction.displayAmount || transaction.amount), transaction.currency || 'EUR')}
            </div>
            <div className="text-xs text-muted-foreground">
              {transaction.date ? new Date(transaction.date).toLocaleDateString() : 'No date'}
            </div>
          </div>
        </div>
        {transaction.description && (
          <p className="text-xs sm:text-sm text-muted-foreground mt-2">{transaction.description}</p>
        )}
      </CardHeader>
    </Card>
  )
}

export const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  categoryBalances = [],
  isLoading,
  isError,
  onAddExpense,
  onEditExpense,
  onDeleteExpense,
  onLoadMore,
  hasMore,
  isLoadingMore,
  groupBy: groupByOption = 'date',
  showFilters = true,
  highlightId,
}) => {
  const loadMoreRef = React.useRef<HTMLDivElement>(null)
  const currentUserId = useCurrentUser()?.id
  const [selectedFilters, setSelectedFilters] = React.useState<{
    type?: 'expense' | 'settlement' | 'pool_event' | 'all'
    dateRange?: string
  }>({ type: 'all' })

  React.useEffect(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore()
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    )

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [onLoadMore, hasMore, isLoadingMore])

  if (isLoading) {
    return <ExpenseListSkeleton />
  }

  if (isError) {
    return (
      <EmptyState
        title="Failed to load transactions"
        description="Something went wrong while loading your transactions. Please try again."
        action={{
          label: "Retry",
          onClick: () => window.location.reload(),
        }}
      />
    )
  }

  if (transactions.length === 0) {
    return (
      <EmptyState
        icon={<Plus className="h-6 w-6" />}
        title="No transactions yet"
        description="Start tracking your group expenses by adding your first expense."
        action={
          onAddExpense
            ? {
                label: "Add Expense",
                onClick: onAddExpense,
              }
            : undefined
        }
      />
    )
  }

  const filteredTransactions = selectedFilters.type === 'all'
    ? transactions
    : transactions.filter(t => t.type === selectedFilters.type)

  // Wrap each card with a stable DOM anchor (id="txn-<id>") and a temporary
  // highlight ring so deep links / search can scroll to and flag a row.
  const renderCard = (transaction: Transaction, canEdit: boolean) => {
    const card = transaction.type === 'settlement' ? (
      <SettlementCard transaction={transaction} />
    ) : transaction.type === 'pool_event' ? (
      <PoolEventCard transaction={transaction} />
    ) : (
      <ExpenseCard
        expense={transaction as any}
        onEdit={canEdit ? onEditExpense : undefined}
        onDelete={canEdit ? onDeleteExpense : undefined}
        showCategory={true}
      />
    )

    return (
      <div
        key={transaction.id}
        id={`txn-${transaction.id}`}
        className={
          highlightId === transaction.id
            ? "rounded-xl ring-2 ring-primary ring-offset-2 ring-offset-background transition-shadow scroll-mt-24"
            : "scroll-mt-24"
        }
      >
        {card}
      </div>
    )
  }

  const renderTransactionGroups = () => {
    // First, sort all transactions by date (newest first)
    const sortedTransactions = [...filteredTransactions].sort((a, b) => {
      const dateA = new Date(a.date || 0)
      const dateB = new Date(b.date || 0)
      return dateB.getTime() - dateA.getTime()
    })

    if (groupByOption === 'none') {
      return (
        <div className="space-y-3">
          {sortedTransactions.map((transaction) => renderCard(transaction, true))}
        </div>
      )
    }

    // Group the already-sorted transactions
    const groups = groupByOption === 'date'
      ? groupBy(sortedTransactions, (transaction) => {
          const dateField = transaction.date
          if (!dateField) return 'Unknown Date'
          const date = new Date(dateField)
          return isNaN(date.getTime()) ? 'Unknown Date' : date.toDateString()
        })
      : groupBy(sortedTransactions, (transaction) =>
          transaction.type === 'settlement'
            ? 'Settlements'
            : transaction.type === 'pool_event'
              ? 'Pool Events'
              : 'Expenses'
        )

    // Sort group keys (dates or types)
    const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
      if (groupByOption === 'date') {
        // For date groups, parse and compare dates (newest first)
        if (a === 'Unknown Date') return 1
        if (b === 'Unknown Date') return -1
        const dateA = new Date(a)
        const dateB = new Date(b)
        return dateB.getTime() - dateA.getTime()
      }
      // For type groups, show expenses first
      return a === 'Expenses' ? -1 : 1
    })

    const canModify = (transaction: Transaction) => {
      if (transaction.type !== 'expense') {
        return false
      }
      return transaction.paid_by === currentUserId || transaction.created_by === currentUserId
    }

    return (
      <div>
        {/* Category Summary Cards */}
        {categoryBalances.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
              <Pin className="h-3 w-3" />
              <span>Pinned categories</span>
            </div>
            <div className="space-y-3">
              {categoryBalances.map(category => (
                <CategorySummaryCard
                  key={category.categoryId}
                  categoryName={category.categoryName}
                  categoryEmoji={category.categoryEmoji}
                  balances={category.balances}
                  currencyCode={transactions[0]?.currency || '€'}
                />
              ))}
            </div>
          </div>
        )}

        {/* Transaction List */}
        <div className="space-y-8">
          {sortedGroupKeys.map((groupKey) => {
            const groupTransactions = groups[groupKey as keyof typeof groups]

            return (
              <div key={groupKey} className="space-y-2 sm:space-y-3">
                <h3 className="text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wide px-3 sm:px-0">
                  {groupByOption === 'date'
                    ? formatGroupDate(groupKey)
                    : groupKey
                  }
                </h3>
                <div className="space-y-3 sm:space-y-3">
                  {groupTransactions.map((transaction: Transaction) =>
                    renderCard(transaction, canModify(transaction))
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {showFilters && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
            <Button
              variant={selectedFilters.type === 'all' ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedFilters({ ...selectedFilters, type: 'all' })}
              className="text-xs sm:text-sm px-2 sm:px-3"
            >
              All
            </Button>
            <Button
              variant={selectedFilters.type === 'expense' ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedFilters({ ...selectedFilters, type: 'expense' })}
              className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
            >
              <span className="hidden sm:inline">Expenses Only</span>
              <span className="sm:hidden">Expenses</span>
            </Button>
            <Button
              variant={selectedFilters.type === 'settlement' ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedFilters({ ...selectedFilters, type: 'settlement' })}
              className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
            >
              <span className="hidden sm:inline">Settlements Only</span>
              <span className="sm:hidden">Settlements</span>
            </Button>
            <Button
              variant={selectedFilters.type === 'pool_event' ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedFilters({ ...selectedFilters, type: 'pool_event' })}
              className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
            >
              <span className="hidden sm:inline">Pool Events Only</span>
              <span className="sm:hidden">Pool</span>
            </Button>
          </div>
        </div>
      )}

      {renderTransactionGroups()}

      {hasMore && (
        <div ref={loadMoreRef} className="flex justify-center py-4">
          {isLoadingMore ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Loading more transactions...
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={onLoadMore}
              className="text-muted-foreground"
            >
              Load more
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function formatGroupDate(dateString: string): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  } else {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }
}
